/**
 * The home page's hero film: the photograph starts to move once the page has loaded.
 *
 * <p>This file is the gate and nothing else. It decides <i>whether</i> the film plays, by the rule in
 * `hero-film-rule.ts`, and owns everything the deliveries share — the fade, the stop button, and
 * giving the photograph back when nothing starts. <i>How</i> it plays belongs to a player:
 * `hero-film-video.ts` for a film this site serves, `hero-film-youtube.ts` for one YouTube serves.
 * The partial that rendered the layer decided which, and it said so in the layer's attributes.</p>
 *
 * <p>The film is layered over the photograph rather than put in its place, so the image stays the
 * page's LCP element and there is no black box while it loads. Since 2026-09-29 the film plays from
 * the first visit, so it waits for the window's load event: nothing it fetches may compete with the
 * photograph for the connection.</p>
 *
 * <p><b>Nothing is shown until a player reports a picture.</b> Fading in on a load event was wrong
 * twice over with the embedded player: it is still black at that point, and when a browser refuses
 * autoplay it never leaves that state — it draws its own poster and a large red play button across
 * the hero instead. Both were photographed in testing. Waiting for a real picture means the
 * photograph simply stays when playback is refused, which is the right outcome and not a
 * fallback.</p>
 *
 * <p><b>The stop lasts for this page view and is not remembered.</b> It was stored at first, and that
 * made the button a one-way door — pressing it removed the film for good. A reload brings the film
 * back instead, which is what someone who presses stop expects anyway. The visitor who never wants
 * the motion is covered by `prefers-reduced-motion`, which withholds the film entirely.</p>
 */
import { find, prefersReducedMotion } from "./dom";
import { hasEmbedConsent } from "./consent";
import type { HeroFilmPlayer } from "./hero-film-player";
import { mayPlay, type HeroFilmDelivery } from "./hero-film-rule";
import { embeddedHeroFilm } from "./hero-film-youtube";
import { selfHostedHeroFilm } from "./hero-film-video";

/**
 * The visit count this file kept until 2026-09-29, under consent to embedded content.
 *
 * <p>Nothing reads it any more. It is removed on every visit to any page, so a visitor who allowed
 * it once does not keep a value in their browser that the site no longer uses.</p>
 */
const RETIRED_VISIT_COUNT = "amund.visits.home";

/** The seam the hero already has: below it the card stacks under a 16:9 photograph. */
const WIDE = "(min-width: 900px)";

/** How long to wait for a picture before giving the photograph back. */
const PATIENCE = 4000;

/** Removes a stored string, and shrugs when the store refuses. */
function forget(key: string): void {
  try {
    window.localStorage.removeItem(key);
  } catch {
    // Nothing could have been stored either.
  }
}

/** Runs the callback once the window has loaded, at once when it already has. */
function afterLoad(callback: () => void): void {
  if (document.readyState === "complete") {
    callback();
    return;
  }

  window.addEventListener("load", callback, { once: true });
}

/**
 * True when the visitor has asked their browser to save data.
 *
 * <p>A hero film is decoration, and decoration is the first thing that should go when someone has
 * said they are paying for their megabytes. Not in the DOM typings, hence the cast.</p>
 */
function savingData(): boolean {
  const connection = (navigator as Navigator & { connection?: { saveData?: boolean } }).connection;
  return connection?.saveData === true;
}

/**
 * Returns how the layer's attributes say the film is delivered, or null when they say nothing.
 *
 * <p>A layer carries exactly one of these attributes, because exactly one partial rendered it.</p>
 */
function deliveryOf(layer: HTMLElement): HeroFilmDelivery | null {
  if ((layer.dataset["heroFilmVideo"] ?? "") !== "") {
    return "self-hosted";
  }

  return (layer.dataset["heroFilmYoutube"] ?? "") !== "" ? "youtube" : null;
}

/**
 * Returns the player for the delivery, built from the layer's attributes.
 *
 * <p>One branch per delivery, and the only place in the bundle where both players are named.</p>
 */
function playerFor(layer: HTMLElement, delivery: HeroFilmDelivery): HeroFilmPlayer {
  return delivery === "self-hosted"
    ? selfHostedHeroFilm(layer.dataset["heroFilmVideo"] ?? "", layer.dataset["heroFilmType"] ?? "video/webm")
    : embeddedHeroFilm(layer.dataset["heroFilmYoutube"] ?? "");
}

/** Builds the control that takes the photograph back. */
function stopButton(onStop: () => void): HTMLButtonElement {
  const button = document.createElement("button");
  button.type = "button";
  button.className = "hero__film-stop";
  button.innerHTML =
    `<svg viewBox="0 0 24 24" width="18" height="18" fill="none" aria-hidden="true">` +
    `<rect x="6" y="6" width="12" height="12" rx="1.5" fill="currentColor"/></svg>` +
    `<span class="sr-only">Stoppa filmen och visa bilden</span>`;
  button.addEventListener("click", onStop);
  return button;
}

/**
 * Runs the film: builds it into the layer, fades it in, and takes it away again when it never starts.
 *
 * <p>Separate from the gate above so that the layer and the player arrive here already known to
 * exist. Everything below closes over them, and a closure over something that might be null is how
 * this kind of code goes wrong.</p>
 */
function run(layer: HTMLElement, player: HeroFilmPlayer): void {
  const controls = document.createElement("div");
  controls.className = "hero__film-controls";

  /** Set once the player has been started, so the patience can be called off again. */
  let patience = 0;

  /** Takes the photograph back and clears up after the player. */
  function stop(): void {
    window.clearTimeout(patience);
    document.removeEventListener("visibilitychange", whenLookedAt);
    player.stop();
    layer.remove();
    controls.remove();
  }

  /** Fades the film in over the photograph. */
  function show(): void {
    layer.classList.add("hero__film--playing");
  }

  /**
   * Settles it: the film is moving, so the photograph is not coming back on its own.
   *
   * <p>The stop button is added here and not on the reveal, so that a film showing its first frame
   * but never starting leaves nothing behind to press — the patience below takes it away
   * instead.</p>
   */
  function running(): void {
    window.clearTimeout(patience);
    if (controls.childElementCount === 0) {
      controls.append(stopButton(stop));
    }
  }

  /**
   * Builds the player into the layer and starts the clock on it.
   *
   * <p>The patience is given up on rather than left to hang: a player that never starts is a browser
   * refusing playback, and the honest answer to that is the photograph the page already has.</p>
   */
  function begin(): void {
    document.removeEventListener("visibilitychange", whenLookedAt);
    patience = window.setTimeout(stop, PATIENCE);
    layer.append(player.element);
    layer.after(controls);
    player.start({ show, running });
  }

  /** Starts once the tab is actually being looked at. */
  function whenLookedAt(): void {
    if (document.visibilityState === "visible") {
      begin();
    }
  }

  // Nothing is fetched for a tab nobody is looking at. A page opened in the background would
  // otherwise pull the film down, reveal it to no one, fail to play — browsers do not play to a
  // hidden tab — and have the layer taken away again before it was ever seen. Waiting costs the
  // visitor nothing and saves the whole download for every background tab that is never read.
  if (document.visibilityState === "visible") {
    begin();
  } else {
    document.addEventListener("visibilitychange", whenLookedAt);
  }
}

/** Decides whether the home page's hero film plays at all, and hands it to `run` once the page has loaded. */
export function initHeroFilm(): void {
  forget(RETIRED_VISIT_COUNT);

  const layer = find("[data-hero-film-video], [data-hero-film-youtube]");
  if (layer === null) {
    return;
  }

  const delivery = deliveryOf(layer);
  if (delivery === null) {
    return;
  }

  // The screen width is tested here and not only in CSS, so a phone loads nothing at all rather
  // than hiding something it downloaded.
  const allowed = mayPlay({
    delivery,
    wide: window.matchMedia(WIDE).matches,
    reducedMotion: prefersReducedMotion(),
    savingData: savingData(),
    embedConsent: hasEmbedConsent(),
  });
  if (!allowed) {
    return;
  }

  const player = playerFor(layer, delivery);
  afterLoad(() => run(layer, player));
}
